import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaTimes, FaEye, FaFileAlt, FaLock } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Providers.css';

const ProviderApproval = () => {
  const navigate = useNavigate();
  const [pendingProviders, setPendingProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [commissionRate, setCommissionRate] = useState('0');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await api.get(ENDPOINTS.PROVIDERS.base, {
        params: { status: statusFilter }
      });
      setPendingProviders(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to fetch providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultCommission = async () => {
    try {
      const response = await api.get(ENDPOINTS.SETTINGS.commission.default);
      setCommissionRate(response.data.value || '0');
    } catch (error) {
      console.warn('Failed to fetch default commission:', error);
      setCommissionRate('0');
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchDefaultCommission();
  }, [statusFilter]);

  const handleApprove = async () => {
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${selectedProvider.id}/approve`, {
        commissionRate: parseFloat(commissionRate)
      });
      toast.success('Provider approved successfully!');
      setShowApproveModal(false);
      fetchProviders();
    } catch (error) {
      console.error('Failed to approve provider:', error);
      toast.error('Failed to approve provider');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.warning('Please provide a rejection reason');
      return;
    }
    try {
      await api.post(`${ENDPOINTS.PROVIDERS.base}/${selectedProvider.id}/reject`, {
        reason: rejectionReason
      });
      setShowModal(false);
      setRejectionReason('');
      toast.success('Provider rejected');
      fetchProviders();
    } catch (error) {
      console.error('Failed to reject provider:', error);
      toast.error('Failed to reject provider');
    }
  };

  const openRejectModal = (provider) => {
    setSelectedProvider(provider);
    setShowModal(true);
  };

  const openApproveModal = (provider) => {
    setSelectedProvider(provider);
    setShowApproveModal(true);
  };

  return (
    <div className="provider-approval-page">
      <div className="page-header">
        <div>
          <h1>Provider Approvals</h1>
          <p className="page-subtitle">Manage provider applications and verification status</p>
        </div>
      </div>

      <div className="status-tabs" style={{ marginBottom: '24px' }}>
        <button 
          className={`status-tab ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          Pending Review
        </button>
        <button 
          className={`status-tab ${statusFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setStatusFilter('approved')}
        >
          Approved
        </button>
        <button 
          className={`status-tab ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          Rejected
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading pending providers...</p>
        </div>
      ) : (
        <>
          <div className="approval-stats">
            <div className="stat-mini-card pending">
              <div className="stat-mini-icon">
                <FaFileAlt />
              </div>
              <div className="stat-mini-content">
                <h3>{pendingProviders.length}</h3>
                <p>{statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Providers</p>
              </div>
            </div>
          </div>

      {pendingProviders.length === 0 ? (
        <div className="content-card">
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <h3>All caught up!</h3>
            <p>No pending provider applications at the moment</p>
          </div>
        </div>
      ) : (
        <div className="approval-grid">
          {pendingProviders.map((provider) => (
            <div key={provider.id} className="approval-card">
              <div className="approval-card-header">
                <img 
                  src={provider.avatar || `https://ui-avatars.com/api/?name=${provider.firstName}+${provider.lastName}`} 
                  alt={`${provider.firstName} ${provider.lastName}`}
                  className="approval-avatar"
                />
                <div className="approval-info">
                  <h3>{provider.firstName} {provider.lastName}</h3>
                  <p className="approval-email">{provider.email}</p>
                  <p className="approval-phone">{provider.phone || 'No phone'}</p>
                </div>
              </div>

              <div className="approval-card-body">
                <div className="approval-detail">
                  <label>Services Offered:</label>
                  <div className="services-list">
                    {provider.services?.map((service, idx) => (
                      <span key={idx} className="service-tag">{service.name || service}</span>
                    ))}
                  </div>
                </div>

                <div className="approval-detail">
                  <label>Business Name:</label>
                  <span>{provider.businessName || 'Independent'}</span>
                </div>

                <div className="approval-detail">
                  <label>Hourly Rate:</label>
                  <span className="rate-value">${provider.hourlyRate}/hour</span>
                </div>
                {provider.serviceDetails?.yearsOfExperience && (
                  <div className="approval-detail">
                    <label>Experience:</label>
                    <span>{provider.serviceDetails.yearsOfExperience} Years</span>
                  </div>
                )}

                <div className="approval-detail">
                  <label>Applied Date:</label>
                  <span>{new Date(provider.createdAt).toLocaleDateString()}</span>
                </div>

                <div className="approval-detail">
                  <label>Profile Status:</label>
                  <span className={`badge badge-${
                    provider.bio ? 'success' : 'warning'
                  }`}>
                    {provider.bio ? 'Complete' : 'Incomplete'}
                  </span>
                </div>

                <div className="approval-detail bio-section">
                  <label>Biography:</label>
                  <p className="provider-bio-text">{provider.serviceDetails?.aboutMe || provider.bio}</p>
                </div>
              </div>

              <div className="approval-card-footer">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => navigate(`/providers/${provider.id}`)}
                >
                  <FaEye /> View Documents
                </button>
                <div className="approval-actions">
                  {provider.status === 'pending' ? (
                    <>
                      <button 
                        onClick={() => openRejectModal(provider)}
                        className="btn btn-danger btn-sm"
                      >
                        <FaTimes /> Reject
                      </button>
                      <button 
                        onClick={() => openApproveModal(provider)}
                        className="btn btn-success btn-sm"
                      >
                        <FaCheckCircle /> Approve
                      </button>
                    </>
                  ) : (
                    <button 
                      onClick={() => navigate(`/providers/${provider.id}`)}
                      className="btn btn-primary btn-sm"
                    >
                      <FaEye /> View Details
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )}

    {/* Rejection Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Provider Application</h3>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>
                You are about to reject the application of{' '}
                <strong>{selectedProvider?.firstName} {selectedProvider?.lastName}</strong>
              </p>
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejection..."
                  rows="5"
                  className="form-control"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleReject}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApproveModal && (
        <div className="modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Provider</h3>
              <button 
                className="modal-close"
                onClick={() => setShowApproveModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>
                You are about to approve the application of{' '}
                <strong>{selectedProvider?.firstName} {selectedProvider?.lastName}</strong>
              </p>
              <div className="form-group" style={{ marginTop: '15px' }}>
                <label>Set Commission Rate (%)</label>
                <input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  className="form-control"
                  min="0"
                  max="100"
                  step="0.5"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowApproveModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-success"
                onClick={handleApprove}
              >
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderApproval;